import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { translation as srLatnTranslation } from './locales/sr-Latn/translation';
import { translation as srCyrlTranslation } from './locales/sr-Cyrl/translation';

export type AppLanguage = 'sr-Latn' | 'sr-Cyrl';

export const SUPPORTED_LANGUAGES: readonly AppLanguage[] = ['sr-Latn', 'sr-Cyrl'] as const;

export const DEFAULT_LANGUAGE: AppLanguage = 'sr-Latn';

// Fallback when a key is missing in the active language. sr-Cyrl is the
// authoritative source of truth for HR translations.
export const FALLBACK_LANGUAGE: AppLanguage = 'sr-Cyrl';

const LANGUAGE_STORAGE_KEY = 'erp.language';

export const normalizeAppLanguage = (value: string | null | undefined): AppLanguage | null => {
  if (!value) return null;

  const normalized = value.trim();
  if (!normalized) return null;

  // Accept a few common variants we might receive from storage / backend.
  const lowered = normalized.toLowerCase();

  if (lowered === 'sr-latn' || lowered === 'sr-latn-rs' || lowered === 'sr_latn') return 'sr-Latn';
  if (lowered === 'sr-cyrl' || lowered === 'sr-cyrl-rs' || lowered === 'sr_cyrl') return 'sr-Cyrl';

  // If we ever get just "sr" or "sr-RS", treat it as the product default (Latin).
  if (lowered === 'sr' || lowered === 'sr-rs') return DEFAULT_LANGUAGE;

  if (SUPPORTED_LANGUAGES.includes(normalized as AppLanguage)) return normalized as AppLanguage;
  return null;
};

export const getInitialAppLanguage = (): AppLanguage => {
  // Product requirement: default Serbian Latin unless user explicitly chose otherwise.
  const fromStorage = normalizeAppLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  return fromStorage ?? DEFAULT_LANGUAGE;
};

export const setAppLanguage = async (language: AppLanguage) => {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  document.documentElement.lang = language;
  await i18n.changeLanguage(language);
};

let isInitialized = false;

export const initI18n = async () => {
  if (isInitialized) return i18n;
  isInitialized = true;

  const initialLanguage = getInitialAppLanguage();
  document.documentElement.lang = initialLanguage;

  // Lazy import to avoid a cycle between this module and the per-module slices.
  const { MODULE_TRANSLATIONS, deepMerge } = await import('./moduleTranslations');

  const mergedSrLatn = deepMerge(
    { ...srLatnTranslation } as Record<string, unknown>,
    ...MODULE_TRANSLATIONS['sr-Latn']
  );
  const mergedSrCyrl = deepMerge(
    { ...srCyrlTranslation } as Record<string, unknown>,
    ...MODULE_TRANSLATIONS['sr-Cyrl']
  );

  await i18n
    .use(initReactI18next)
    .init({
      lng: initialLanguage,
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      interpolation: { escapeValue: false },
      resources: {
        'sr-Latn': { translation: mergedSrLatn },
        'sr-Cyrl': { translation: mergedSrCyrl },
      },
      react: {
        useSuspense: false,
      },
    });

  // Browser tab title follows the selected script (Latin / Cyrillic).
  const syncDocumentTitle = () => {
    document.title = i18n.t('app.title');
  };
  syncDocumentTitle();
  i18n.on('languageChanged', syncDocumentTitle);

  return i18n;
};

export { i18n };

